import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
/** Whether the once-per-page-load redirect check has run; morph() re-fires connectedCallback. @type {boolean} */
let redirectCheckComplete = false;

class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {(event: Event) => void} */
  #boundVariantChanged = this.variantChanged.bind(this);

  connectedCallback() {
    super.connectedCallback();

    // Prevent duplicate state when reconnecting after morph()
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.addEventListener('change', this.#boundVariantChanged);

    this.redirectToConnectedProductIfNeeded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#boundVariantChanged);
    this.#abortController?.abort();
  }

  /** Removes the Liquid `visibility: hidden` that prevents a flash of the wrong option before redirect. */
  #revealPicker() {
    this.style.removeProperty('visibility');
  }

  redirectToConnectedProductIfNeeded() {
    // Only once per page load: morph() re-triggers connectedCallback on every child product switch.
    if (redirectCheckComplete) {
      // Defensive: ensure the picker is visible after morph re-connections.
      this.#revealPicker();
      return;
    }
    redirectCheckComplete = true;

    const isMainProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isMainProductPage) {
      this.#revealPicker();
      return;
    }

    const primaryRadios = this.#radios[0];

    // Covers child URLs both with and without ?variant= param.
    const currentPathname = window.location.pathname;
    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const isAlreadyOnChildProduct =
      currentProductUrl === currentPathname ||
      primaryRadios?.some((radio) => radio.dataset.connectedProductUrl?.split('?')[0] === currentPathname);
    if (isAlreadyOnChildProduct) {
      this.#revealPicker();
      return;
    }

    // Always use the first radio (index 0) regardless of which option Shopify pre-selects.
    const firstOption = primaryRadios?.[0];
    const connectedUrl = firstOption?.dataset.connectedProductUrl?.split('?')[0];

    if (!connectedUrl || connectedUrl === currentPathname) {
      this.#revealPicker();
      return;
    }

    // Redirect — keep the picker hidden since the page is navigating away.
    const destination = new URL(connectedUrl, window.location.origin);
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) destination.searchParams.set('view', viewParamValue);

    window.location.replace(destination.toString());
  }

  /** @param {Event} event - The variant change event. */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Combined Listings child products need all of main morphed: other sections depend on the product.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const morphElementSelector = loadsNewProduct ? "main" : undefined;

    // A primary (fieldset 0) change within the same product re-renders the secondary option labels.
    const changedFieldsetIndex = parseInt(event.target.dataset.fieldsetIndex ?? '-1', 10);
    const isPrimaryOptionChange = !loadsNewProduct && changedFieldsetIndex === 0;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, isPrimaryOptionChange);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via Combined Listings.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /** @param {string | Element} target - The target element. */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }

          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Apply the new states
          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // Combined Listings use separate URLs, so track the pending one and abort a fast repeat request.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (selectedOption.dataset.connectedProductUrl && !source) {
      // Product switch: stale option_values cause a server no-match, so let it render its default.
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {string} requestUrl
   * @param {string} [morphElementSelector] - Element to morph; defaults to the variant picker only.
   * @param {boolean} [isPrimaryOptionChange] - Whether the picker needs re-morphing.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, isPrimaryOptionChange = false) {
    // Abort the previous fetch request, if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        let parsedVariant = null;
        let fallbackData = null;

        try {
          parsedVariant = JSON.parse(textContent);
        } catch (error) {
          console.error('Failed to parse variant picker JSON content:', error);
        }

        // Recovery branch
        if (!parsedVariant) {
          const fallbackTextContent = html.querySelector(`script[data-json="product-variants"]`)?.textContent;

          try {
            fallbackData = JSON.parse(fallbackTextContent);
          } catch (error) {
            console.error("Failed to parse fallback JSON:", error);
            return;
          }

          this.resetToFirstAvailableVariant(this.selectedOption, fallbackData);
          return;
        }

        // Success
        if (morphElementSelector === 'main') {
          // Collect the checked optionValueIds before morph replaces the live elements.
          const checkedValueIds = new Set(
            Array.from(html.querySelectorAll('variant-picker input[data-current-checked="true"]'))
              .map((input) => input.dataset.optionValueId)
              .filter(Boolean)
          );

          // Capture recommendation URLs before the morph to detect which changed product.
          const previousRecommendationUrls = this.collectRecommendationUrls();

          // Morph all of main: recommendations, metafields and other sections depend on the product.
          this.updateMain(html);

          this.reloadProductRecommendations(previousRecommendationUrls);

          // Re-apply .checked on the morphed radios so the :checked styling is correct.
          checkedValueIds.forEach((id) => {
            const liveInput = this.querySelector(`input[data-option-value-id="${id}"]`);
            if (liveInput) liveInput.checked = true;
          });

          this.updateAffirm(parsedVariant?.price);
          yotpoWidgetsContainer?.initWidgets();
          window.publish(PUB_SUB_EVENTS.vehicleData);
        } else {
          // Regular variant change: update only the variant info.
          this.updateSku(parsedVariant);
          this.updatePrice(html, parsedVariant);
          this.updateOptionsLegend(parsedVariant);

          // Morph the picker so secondary options are shown/hidden correctly.
          if (isPrimaryOptionChange) {
            this.updateVariantPicker(html);
          }
        }

        // Needed regardless of morphing type.
        this.updateProductForm(parsedVariant);
        this.toggleAddButton(parsedVariant);
        this.updateBIS(parsedVariant);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user:', error);
        } else {
          console.error(error);
        }
      });
  }

  /** Ensures a non-existent option combo isn't selected after a Combined Listing product change. */
  resetToFirstAvailableVariant(selectedOption, fallbackData) {
    const product = fallbackData?.find((p) => p.options.includes(selectedOption.value)) || fallbackData?.[0];
    if (!product) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset, index) => {
      const inputToSelect = fieldset.querySelector(`input[value="${product.options[index]}"]`);
      if (!inputToSelect) return;
      const alreadyChecked = inputToSelect.checked;
      this.updateSelectedOption(inputToSelect);
      if (index === fieldsets.length - 1 && !alreadyChecked) {
        inputToSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /** @param {Document} newHtml - The new HTML. */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /** @returns {Map<Element, string | undefined>} Every recommendations element mapped to its `data-url`. */
  collectRecommendationUrls() {
    return new Map(
      Array.from(document.querySelectorAll('product-recommendations'), (element) => [element, element.dataset.url])
    );
  }

  /**
   * Re-fetches recommendations: morph() updates `data-url` in place, so connectedCallback never re-runs.
   * @param {Map<Element, string | undefined>} previousUrls - The `data-url` values captured before the morph.
   */
  reloadProductRecommendations(previousUrls) {
    for (const [element, previousUrl] of previousUrls) {
      if (!element.isConnected) continue;

      const currentUrl = element.dataset.url;
      if (!currentUrl || currentUrl === previousUrl) continue;
      if (typeof element.loadRecommendations !== 'function') continue;

      element.loadRecommendations();
    }
  }

  /** Morphs the picker so secondary options match the newly-selected primary option. @param {Document} newHtml */
  updateVariantPicker(newHtml) {
    const newPicker = newHtml.querySelector('variant-picker');
    if (newPicker) {
      morph(this, newPicker);
    }
  }

  /** Refreshes Affirm with the new product price. @param {Number} price */
  updateAffirm(price) {
    if (!price) return;
    const affirmWidgets = document.querySelectorAll('[data-product-affirm]');
    affirmWidgets.forEach(affirmWidget => {
      affirmWidget.setAttribute('data-amount', price);
    });
    affirm.ui.refresh();
  }

  /** Enables/disables the CTA and updates its text. @param {Object} variant */
  toggleAddButton(variant) {
    const addButtons = document.querySelectorAll(`#product-form-${this.dataset.sectionId} [name="add"], sticky-form [data-product-add-to-cart]`);

    addButtons.forEach(el => {
      if (variant?.available) {
        el.removeAttribute('disabled');
      } else {
        el.setAttribute('disabled', true);
      }

      el.textContent = !variant
        ? window.variantStrings?.unavailable
        : variant.available
          ? window.variantStrings?.addToCart
          : window.variantStrings?.soldOut;
    });
  }

  updateBIS(variant) {
    const bisContainer = document.querySelector('[data-form-holder]');

    if (!bisContainer) return;

    if (!variant || variant.available) {
      bisContainer.classList.remove('product-form-holder--bis');
    } else {
      bisContainer.classList.add('product-form-holder--bis');
    }
  }

  updateSku(variant) {
    const productSku = document.getElementById('productSku');
    const productStickySku = document.getElementById('productStickySku');

    if (!variant || !variant.sku) {
      if (productSku) productSku.innerText = 'SKU:';
      if (productStickySku) productStickySku.innerText = 'SKU:';
      return;
    }
    if (productSku) productSku.innerText = `SKU: ${variant.sku}`;
    if (productStickySku) productStickySku.innerText = `SKU: ${variant.sku}`;
  }

  updateProductForm(variant) {
    if (!variant) return;
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment, #product-form-sticky`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePrice(html, variant) {
    if (!html || !variant) return;
    const sectionId = this.dataset.sectionId;
    const id = `price-${sectionId}`;
    const destination = document.getElementById(`price-${sectionId}`);
    const source = html.getElementById(id);
    if (source && destination) destination.innerHTML = source.innerHTML;
  }

  updateOptionsLegend(variant) {
    if (!variant) {
      const els = this.querySelectorAll(`[data-product-option-active]`);
      if (els.length) els[els.length - 1].innerText = '';
      return;
    }
    variant?.options.forEach((option, index) => {
      const el = this.querySelector(`[data-product-option-active="${index}"]`);
      if (el) el.innerText = option;
    });
  }

  /** @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option. */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /** @returns {string | undefined} The selected option ID. */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /** @returns {string[]} The selected options values. */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}